import { createContext, ReactNode, useContext, useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import { api, Settings, TeamMember } from './api'
import logo from '../assets/polakis-logo.png'
import type { Product } from '../data/products'
import type { Industry } from '../data/industries'
import type { Solution } from '../data/solutions'
import type { Service } from '../data/services'
import type { CaseStudy } from '../data/caseStudies'
import type { Testimonial } from '../data/testimonials'
import type { BlogPost } from '../data/blog'

interface SiteData {
  products: Product[]
  industries: Industry[]
  solutions: Solution[]
  services: Service[]
  caseStudies: CaseStudy[]
  testimonials: Testimonial[]
  blogPosts: BlogPost[]
  team: TeamMember[]
  settings: Settings

  getProductBySlug: (slug: string) => Product | undefined
  getRelatedProducts: (product: Product) => Product[]
  getCaseStudyBySlug: (slug: string) => CaseStudy | undefined
  getBlogPostBySlug: (slug: string) => BlogPost | undefined
}

const emptySettings: Settings = {
  methodology: [],
  techStack: [],
  aiPlatformFeatures: [],
  companyValues: [],
  companyStats: [],
  offices: [],
  partners: [],
  supportChannels: [],
  resourceCategories: [],
  trustBarLogos: [],
  statHighlights: [],
  blogCategories: [],
}

const SiteDataContext = createContext<SiteData | null>(null)

export function SiteDataProvider({ children }: { children: ReactNode }) {
  const results = useQueries({
    queries: [
      { queryKey: ['products'], queryFn: api.products, staleTime: Infinity },
      { queryKey: ['industries'], queryFn: api.industries, staleTime: Infinity },
      { queryKey: ['solutions'], queryFn: api.solutions, staleTime: Infinity },
      { queryKey: ['services'], queryFn: api.services, staleTime: Infinity },
      { queryKey: ['caseStudies'], queryFn: api.caseStudies, staleTime: Infinity },
      { queryKey: ['testimonials'], queryFn: api.testimonials, staleTime: Infinity },
      { queryKey: ['blogPosts'], queryFn: api.blogPosts, staleTime: Infinity },
      { queryKey: ['team'], queryFn: api.team, staleTime: Infinity },
      { queryKey: ['settings'], queryFn: api.settings, staleTime: Infinity },
    ],
  })

  const [
    productsQ, industriesQ, solutionsQ, servicesQ, caseStudiesQ,
    testimonialsQ, blogPostsQ, teamQ, settingsQ,
  ] = results

  const isLoading = results.some((r) => r.isLoading)
  const isError = results.some((r) => r.isError)

  const value = useMemo<SiteData | null>(() => {
    if (isLoading) return null
    const products = productsQ.data ?? []
    const caseStudies = caseStudiesQ.data ?? []
    const blogPosts = blogPostsQ.data ?? []
    return {
      products,
      industries: industriesQ.data ?? [],
      solutions: solutionsQ.data ?? [],
      services: servicesQ.data ?? [],
      caseStudies,
      testimonials: testimonialsQ.data ?? [],
      blogPosts,
      team: teamQ.data ?? [],
      settings: settingsQ.data ?? emptySettings,
      getProductBySlug: (slug) => products.find((p) => p.slug === slug),
      getRelatedProducts: (product) =>
        product.relatedSlugs.map((slug) => products.find((p) => p.slug === slug)).filter((p): p is Product => Boolean(p)),
      getCaseStudyBySlug: (slug) => caseStudies.find((c) => c.slug === slug),
      getBlogPostBySlug: (slug) => blogPosts.find((b) => b.slug === slug),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, productsQ.data, industriesQ.data, solutionsQ.data, servicesQ.data, caseStudiesQ.data, testimonialsQ.data, blogPostsQ.data, teamQ.data, settingsQ.data])

  if (isError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-ink-900 px-6 text-center text-white">
        <img src={logo} alt="Polakis Technologies" className="h-10 w-auto brightness-0 invert" />
        <p className="max-w-sm text-sm text-ink-300">
          We couldn't reach the Polakis content service. Make sure the backend is running, then refresh this page.
        </p>
      </div>
    )
  }

  if (!value) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-900">
        <img src={logo} alt="Polakis Technologies" className="h-9 w-auto animate-pulse brightness-0 invert" />
      </div>
    )
  }

  return <SiteDataContext.Provider value={value}>{children}</SiteDataContext.Provider>
}

export function useSiteData(): SiteData {
  const ctx = useContext(SiteDataContext)
  if (!ctx) {
    throw new Error('useSiteData must be used within a SiteDataProvider')
  }
  return ctx
}
